import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useInventorySuppliers } from '../../../hooks/inventory/useInventorySuppliers';
import { useDeleteInventorySupplier } from '../../../hooks/inventory/useDeleteInventorySupplier';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getInventorySupplierById } from '../../../api/inventoryService';
import InventorySupplierForm from '../forms/InventorySupplierForm';
import InventorySupplierView from './InventorySupplierView';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InventorySuppliers() {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetParent();
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");

    const [selectedSupplier, setSelectedSupplier] = React.useState(null);

    const handleListItemClick = (supplier) => {
        setViewOpen(true);
        setSelectedSupplier(supplier);
    };

    const { showSnackbar } = useSnackbar();

    const { suppliers, loading, loadMore, searchFunction, resetParent } = useInventorySuppliers(open, 1, 10);

    const { remove } = useDeleteInventorySupplier(resetParent);

    const handleAdd = () => {
        setSelectedSupplier(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (supplier) => {
        setSelectedSupplier(supplier);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = async (savedSupplier) => {
        await resetParent();
        const updatedSupplier = await getInventorySupplierById(savedSupplier.id);
        setSelectedSupplier(updatedSupplier);
        if (!viewOpen) setViewOpen(true);
    };

    const handleDelete = async (supplier) => {
        try {
            await remove(supplier.id);
            showSnackbar(t('inventory.suppliers.messages.delete_success', { name: supplier.name }), "success");
        } catch (err) {
            if (err.status === 409) {
                showSnackbar(t('inventory.suppliers.messages.delete_fail_in_use', { name: supplier.name }), "error");
            } else {
                showSnackbar(t('inventory.suppliers.messages.delete_fail', { name: supplier.name }), "error");
            }
        }
    };

    return (
        <React.Fragment>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <StorefrontIcon fontSize='large' color='secondary'/>
            </ListItemIcon>
            <ListItemText 
                primary="Suppliers"
                secondary="view and manage inventory suppliers"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
                <Typography variant="h6">Inventory Suppliers</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                <List sx={{ minHeight: 500 }}>
                    {suppliers?.data?.map((supplier) => (
                    <ListItem
                        key={supplier.id}
                        secondaryAction={
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                    id: 1, 
                                    name: 'View',
                                    callback: () => handleListItemClick(supplier),
                                    disabled: !userHasAccess(SUB_MODULES.SUPPLIERS, 1)
                                },
                                { 
                                    id: 2, 
                                    name: 'Edit',
                                    callback: () => handleEdit(supplier),
                                    disabled: !userHasAccess(SUB_MODULES.SUPPLIERS, 2)
                                },
                                {
                                    id: 3,
                                    name: 'Delete',
                                    callback: () => handleDelete(supplier),
                                    confirmMessage: t('inventory.suppliers.messages.delete_confirm', { name: `${supplier.name}` }),
                                    disabled: !userHasAccess(SUB_MODULES.SUPPLIERS, 3)
                                },
                            ]}
                            />
                        </Box>
                        }
                    >
                        <ListItemButton onClick={() => {if (userHasAccess(SUB_MODULES.SUPPLIERS, 2)) handleListItemClick(supplier)}}>
                            <ListItemIcon>
                                <StorefrontIcon fontSize='large' color='primary' />
                            </ListItemIcon>
                            <ListItemText
                            primary={`${supplier.name}`}
                            secondary={ supplier.contact_person ? supplier.contact_person : "" }
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>

                {/* Load More only shows if more pages exist */}
                {suppliers?.pagination?.page < suppliers?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleAdd}>Add Supplier</Button>
                <div style={{flex: '1 0 0'}} />
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InventorySupplierView 
            handleEdit={handleEdit}
            supplier={selectedSupplier}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <InventorySupplierForm 
            successCallback={onSaveSuccess}
            supplier={selectedSupplier}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        </React.Fragment>
    );
}